import { DeleteForever, MoreVert } from '@mui/icons-material';
import { FormattedMessage, formatTranslation, userRoleLabelKey, unsafeTranslationKey } from 'intl';
import { CommonTranslationKeys, FiltersTranslationKeys, NotificationTranslationKeys, UserTranslationKeys } from 'intl/keys';
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import ViewWrapper from 'components/core/Layout/ViewWrapper';
import { useOrganizations } from 'store/organizations';
import { SubheaderText } from 'components/general';
import { MemoForbiddenTooltip } from 'components/general/ForbiddenTooltip/ForbiddenTooltip';
import {
  MemoPreviewField,
  MemoPreviewPhoneField,
} from 'components/general/PreviewField';
import {
  inspectionSectionPaddingSx,
  inspectionSubheaderBaseSx,
} from 'components/inspection/layout';
import { useAppNavigation } from 'contexts/navigation';
import { usePermissions } from 'hooks/permissions/usePermissions';
import useIsMobile from 'hooks/useIsMobile';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useProject } from 'store/project';
import { useProjectUsersActions } from 'store/projectUsers';
import { PermissionsModel } from 'shared/domain/user/types/model';
import { UserOnView } from 'shared/domain/user/types/view';
import { CreatableUserRole, UserRole } from 'shared/types/userRole';
import { Color } from 'shared/utils/colors';

const REVOKE_DIALOG_TITLE_KEY = {
  organization: UserTranslationKeys.revokeInOrganizationDialogTitle,
  project: UserTranslationKeys.revokeInProjectDialogTitle,
} as const;

const REVOKE_TOOLTIP_KEY = {
  organization: NotificationTranslationKeys.revokeInOrganization,
  project: NotificationTranslationKeys.revokeInProject,
} as const;

type SingleUserProps = UserOnView;

export function SingleUser(props: SingleUserProps): React.ReactElement {
  const intl = useIntl();
  const { goToUserEdit } = useAppNavigation();
  const { canEditUser, canDeleteUser } = usePermissions();

  const {
    label,
    phone,
    email,
    processes,
    role,
    sites,
    _id: userId,
  } = props;

  const editPermission = useMemo(
    () => canEditUser(userId, role._id),
    [canEditUser, userId, role._id]
  );

  const deletePermission = useMemo(
    () => canDeleteUser(userId, role._id),
    [canDeleteUser, userId, role._id]
  );

  const isClientAdmin = role._id === UserRole.organizationAdmin;
  const isNoAccess = role._id === UserRole.visible_no_access;
  const canRevoke = !isNoAccess && (editPermission.permission || deletePermission.permission);
  const revokeLabelId = isClientAdmin ? 'revoke_permissions_in_this_organization' : 'revoke_permissions_in_this_project';
  const removeCompletelyLabelId = isClientAdmin
    ? 'remove_user_completely_from_organization'
    : 'remove_user_completely_from_project';
  const removeCompletelyTooltipId = isClientAdmin
    ? 'tooltip_remove_user_completely_from_organization'
    : 'tooltip_remove_user_completely_from_project';
  const removeCompletelyDialogTitleId = isClientAdmin
    ? 'remove_user_completely_from_organization_dialog_title'
    : 'remove_user_completely_from_project_dialog_title';
  const removeCompletelyDialogConsequencesId = isClientAdmin
    ? 'remove_user_completely_from_organization_dialog_consequences'
    : 'remove_user_completely_from_project_dialog_consequences';

  const { organizationId, _id: projectId } = useProject();
  const { organizationsMap } = useOrganizations();
  const currentOrganization = organizationsMap.get(organizationId);
  const createDialog = useDialog();
  const { goToUsers } = useAppNavigation();
  const { revokeAccess, remove } = useProjectUsersActions();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleEdit = useCallback(() => {
    handleMenuClose();
    goToUserEdit(userId);
  }, [userId, goToUserEdit, handleMenuClose]);

  const revokePermissionsPayload = useMemo(
    (): PermissionsModel => ({
      clients: [
        {
          _id: organizationId,
          projects: [
            {
              _id: projectId,
              role: UserRole.visible_no_access as const,
            },
          ],
        },
      ],
    }),
    [organizationId, projectId]
  );

  const handleRevoke = useCallback(() => {
    handleMenuClose();
    const isOrg = isClientAdmin;
    const title = formatTranslation(
      intl,
      REVOKE_DIALOG_TITLE_KEY[isOrg ? 'organization' : 'project']
    );
    const description = isOrg ? (
      <FormattedMessage
        id={UserTranslationKeys.revokeInOrganizationDialogBody}
        values={{
          user: (
            <Typography component='strong' sx={{ color: Color.red }}>
              {label}
            </Typography>
          ),
          clientName: currentOrganization?.name || '',
        }}
      />
    ) : (
      <FormattedMessage
        id={UserTranslationKeys.revokeInProjectDialogBody}
        values={{
          user: (
            <Typography component='strong' sx={{ color: Color.red }}>
              {label}
            </Typography>
          ),
        }}
      />
    );
    createDialog({
      title,
      description: <span>{description}</span>,
      submitLabel: formatTranslation(intl, CommonTranslationKeys.ok),
    })
      .then(() => {
        revokeAccess({
          _id: userId,
          email,
          organizationId,
          projectId,
          permissions: revokePermissionsPayload,
        });
        goToUsers();
      })
      .catch(() => {});
  }, [
    handleMenuClose,
    intl,
    label,
    isClientAdmin,
    currentOrganization?.name,
    createDialog,
    userId,
    email,
    organizationId,
    projectId,
    revokePermissionsPayload,
    revokeAccess,
    goToUsers,
  ]);

  const handleRemoveCompletely = useCallback(() => {
    handleMenuClose();
    const title = formatTranslation(intl, unsafeTranslationKey(removeCompletelyDialogTitleId) );
    const consequencesWarning = formatTranslation(intl, unsafeTranslationKey(removeCompletelyDialogConsequencesId),);
    createDialog({
      title,
      description: (
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {consequencesWarning}
        </Typography>
      ),
      submitLabel: formatTranslation(intl, CommonTranslationKeys.ok),
      isDangerousAction: true,
    })
      .then(() => {
        remove({
          _id: userId,
          role: isClientAdmin ? UserRole.organizationAdmin : (role._id as CreatableUserRole),
          organizationId,
          projectId,
        });
        goToUsers();
      })
      .catch(() => {});
  }, [
    handleMenuClose,
    intl,
    createDialog,
    userId,
    organizationId,
    projectId,
    isClientAdmin,
    role._id,
    remove,
    goToUsers,
    removeCompletelyDialogTitleId,
    removeCompletelyDialogConsequencesId,
  ]);

  const tooltipRevoke = useMemo(() => {
    if (!canRevoke) return formatTranslation(intl, NotificationTranslationKeys.revokeInBothDisabledNoAccess);
    return formatTranslation(
      intl,
      REVOKE_TOOLTIP_KEY[isClientAdmin ? 'organization' : 'project']
    );
  }, [canRevoke, isClientAdmin, intl]);

  return (
    <ViewWrapper>
      <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{
            ...inspectionSectionPaddingSx,
            mt: 4,
            flexWrap: 'nowrap',
          }}
        >
          <Stack direction='row' alignItems='center' spacing={1}>
            <SubheaderText
              sx={{
                ...inspectionSubheaderBaseSx,
                paddingLeft: 0,
                paddingRight: 0,
              }}
            >
              {label}
            </SubheaderText>
          </Stack>

          <Stack
            direction='row'
            spacing={1}
            justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
          >
            <MemoForbiddenTooltip
              visibleWhen={!editPermission.permission && !deletePermission.permission}
              reason={editPermission.reason || deletePermission.reason}
              inlined
            >
              <IconButton
                onClick={handleMenuClick}
                aria-controls={open ? 'user-actions-menu' : undefined}
                aria-haspopup='true'
                aria-expanded={open ? 'true' : undefined}
                disabled={!editPermission.permission && !deletePermission.permission}
              >
                <MoreVert />
              </IconButton>
            </MemoForbiddenTooltip>
            <Menu
              id='user-actions-menu'
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem
                onClick={handleEdit}
                disabled={!editPermission.permission}
              >
                {formatTranslation(intl, CommonTranslationKeys.edit)}
              </MenuItem>
              <Tooltip title={tooltipRevoke} placement='left'>
                <span>
                  <MenuItem
                    onClick={canRevoke ? handleRevoke : undefined}
                    disabled={!canRevoke}
                  >
                    {formatTranslation(intl, unsafeTranslationKey(revokeLabelId) , isClientAdmin ? { clientName: currentOrganization?.name || '' } : {}
                    )}
                  </MenuItem>
                </span>
              </Tooltip>
              <Tooltip title={formatTranslation(intl, unsafeTranslationKey(removeCompletelyTooltipId) )} placement='left'>
                <span>
                  <MenuItem
                    onClick={handleRemoveCompletely}
                    disabled={!deletePermission.permission}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteForever sx={{ mr: 1, fontSize: 20 }} />
                    {formatTranslation(intl, unsafeTranslationKey(removeCompletelyLabelId) )}
                  </MenuItem>
                </span>
              </Tooltip>
            </Menu>
          </Stack>
        </Stack>

        <Divider sx={{ my: 4 }} />

        <Grid
          container
          spacing={3}
          sx={{
            ...inspectionSectionPaddingSx,
            columnGap: {
              xs: 0,
              sm: '10%',
            },
          }}
        >
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
              <MemoPreviewField
                label={formatTranslation(intl, UserTranslationKeys.email)}
                value={email}
                dense={true}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
              <MemoPreviewPhoneField
                label={formatTranslation(intl, UserTranslationKeys.phoneNumber)}
                value={phone}
                dense={true}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
              <MemoPreviewField
                label={formatTranslation(intl, UserTranslationKeys.managementRole,)}
                value={formatTranslation(intl, userRoleLabelKey(role._id))}
                dense={true}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
              <MemoPreviewField
                label={formatTranslation(intl, FiltersTranslationKeys.filterTypeSite,)}
                value={
                  sites && sites.length > 0
                    ? sites.map((site) => site.label).join(', ')
                    : '-'
                }
                dense={true}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
              <MemoPreviewField
                label={formatTranslation(intl, CommonTranslationKeys.process)}
                value={
                  processes && processes.length > 0
                    ? processes.map((process) => process.label).join(', ')
                    : '-'
                }
                dense={true}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </ViewWrapper>
  );
}
